import React, { ReactNode, useState } from "react";
import Header from "@cloudscape-design/components/header";
import { ListUser } from "../data/data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  Container,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
} from "@cloudscape-design/components";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import title from "process";
import { Link } from "react-router-dom";

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  ListUser: ListUser[];
}

const columnDefinitions: TableProps<ListUser>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "id",
    width: 70,
  },
  {
    header: "NIK",
    cell: ({ NIK }) => <Link to={`/admin/users/${NIK}`}>{NIK}</Link>,
    sortingField: "NIK",
    width: 100,
  },
  {
    header: "Name",
    cell: ({ Name }) => Name,
    sortingField: "Name",
    maxWidth: 100,
  },
  {
    header: "Email",
    cell: ({ Email }) => Email,
    sortingField: "Email",
    maxWidth: 150,
  },
  {
    header: "Position",
    cell: ({ Position }) => Position,
    sortingField: "Position",
    maxWidth: 100,
  },
  {
    header: "Tenant",
    cell: ({ tenant }) => tenant,
    sortingField: "Tenant",
    maxWidth: 100,
  },
  {
    header: "Username",
    cell: ({ Username }) => Username,
    sortingField: "Username",
    maxWidth: 100,
  },
  {
    header: "Password",
    cell: ({ Password }) => Password,
    sortingField: "Position",
    maxWidth: 100,
  },

  {
    header: "Access Role",
    cell: ({ AccessRole }) => AccessRole,
    sortingField: "Class",
    minWidth: 100,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly ListUser[] = [],
  selectedItems: readonly ListUser[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VariationTable({ ListUser }: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 20 });
  const [visible, setVisible] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<ListUser>(ListUser, {
    filtering: {
      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },

    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });
  return (
    <>
      <Table<ListUser>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={true}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Vehicle table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              ListUser,
              collectionProps.selectedItems,
              items.length
            )}
          >
            Users
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find User'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confrim'
            cancelLabel='Cancel'
          />
        }
        //selectionType='single'
      />
    </>
  );
}
